import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import {
  Catalog,
  CATALOG_STATUS_LABELS,
  CatalogMaterial,
  CatalogPackage,
  CatalogRowAction,
  CatalogStatus,
  CatalogUploadPlan,
  CatalogUploadRow,
  UNIT_LABELS,
  UnitOfMeasure
} from '../../models/catalog';
import { CatalogService } from '../../../services/catalog.service';

const PAGE_SIZE = 25;

const ACTION_LABELS: Record<CatalogRowAction, string> = {
  CREATE: 'New',
  UPDATE: 'Update',
  UNCHANGED: 'Unchanged',
  RETIRE: 'Retire',
  ERROR: 'Error'
};

type PendingAction = 'publish' | 'archive';

/**
 * One catalog: its state, the CSV that fills it, and what it currently holds.
 *
 * The upload is always two steps, like the tenant import. Choosing a file previews it — the backend
 * writes nothing and reports what every row would do — and only a clean preview offers "Upload",
 * which re-sends the same file to be committed. A new file throws the old preview away, so what is
 * committed is always what was reviewed.
 */
@Component({
  selector: 'app-catalog-detail',
  templateUrl: './catalog-detail.component.html',
  styleUrls: ['./catalog-detail.component.scss']
})
export class CatalogDetailComponent implements OnInit, OnDestroy {

  catalogId!: string;
  catalog: Catalog | null = null;
  loading = true;
  error: string | null = null;

  formOpen = false;

  pendingAction: PendingAction | null = null;
  actionBusy = false;
  actionError: string | null = null;

  downloadingTemplate = false;

  file: File | null = null;
  plan: CatalogUploadPlan | null = null;
  previewing = false;
  committing = false;
  uploadError: string | null = null;
  uploadSuccess: string | null = null;

  materials: CatalogMaterial[] = [];
  materialsLoading = false;
  materialsError: string | null = null;
  search = '';
  page = 0;
  total = 0;
  readonly pageSize = PAGE_SIZE;

  private readonly search$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(private route: ActivatedRoute, private catalogService: CatalogService) {}

  ngOnInit(): void {
    this.catalogId = this.route.snapshot.paramMap.get('id') ?? '';

    this.search$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.page = 0;
        this.loadMaterials();
      });

    this.loadCatalog();
    this.loadMaterials();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Catalog ────────────────────────────────────────────────────────────────

  loadCatalog(): void {
    this.error = null;
    this.catalogService.getCatalog(this.catalogId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (catalog) => {
          this.catalog = catalog;
          this.loading = false;
        },
        error: (err) => {
          this.error = err?.status === 404 ? 'This catalog does not exist.' : 'Could not load the catalog.';
          this.loading = false;
        }
      });
  }

  onSaved(catalog: Catalog): void {
    this.catalog = catalog;
    this.formOpen = false;
  }

  get canPublish(): boolean {
    return !!this.catalog && this.catalog.status !== 'PUBLISHED' && this.catalog.materialCount > 0;
  }

  get canArchive(): boolean {
    return !!this.catalog && this.catalog.status !== 'ARCHIVED';
  }

  get confirmTitle(): string {
    return this.pendingAction === 'publish' ? 'Publish this catalog?' : 'Archive this catalog?';
  }

  get confirmMessage(): string {
    return this.pendingAction === 'publish'
      ? 'Tenants with Material catalogs switched on will see it and can import materials from it.'
      : 'Tenants will no longer see it. Materials they have already imported stay with them.';
  }

  askAction(action: PendingAction): void {
    this.actionError = null;
    this.pendingAction = action;
  }

  confirmAction(): void {
    if (!this.pendingAction || this.actionBusy) {
      return;
    }
    const call = this.pendingAction === 'publish'
      ? this.catalogService.publish(this.catalogId)
      : this.catalogService.archive(this.catalogId);

    this.actionBusy = true;
    call.pipe(takeUntil(this.destroy$)).subscribe({
      next: (catalog) => {
        this.catalog = catalog;
        this.actionBusy = false;
        this.pendingAction = null;
      },
      error: (err) => {
        this.actionError = this.messageOf(err, 'The status change failed.');
        this.actionBusy = false;
        this.pendingAction = null;
      }
    });
  }

  // ── Template ───────────────────────────────────────────────────────────────

  downloadTemplate(): void {
    if (this.downloadingTemplate) {
      return;
    }
    this.downloadingTemplate = true;
    this.catalogService.downloadTemplate()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'catalog-template.csv';
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(url);
          this.downloadingTemplate = false;
        },
        error: () => {
          this.uploadError = 'Could not download the template.';
          this.downloadingTemplate = false;
        }
      });
  }

  // ── Upload ─────────────────────────────────────────────────────────────────

  onFileSelected(file: File): void {
    this.file = file;
    this.resetUpload();
    this.preview();
  }

  onFileRemoved(): void {
    this.file = null;
    this.resetUpload();
  }

  preview(): void {
    if (!this.file || this.previewing) {
      return;
    }
    this.previewing = true;
    this.uploadError = null;
    this.uploadSuccess = null;

    this.catalogService.previewUpload(this.catalogId, this.file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (plan) => {
          this.plan = plan;
          this.previewing = false;
        },
        error: (err) => {
          this.uploadError = this.messageOf(err, 'Could not read the file.');
          this.previewing = false;
        }
      });
  }

  commit(): void {
    if (!this.file || !this.plan || this.committing || this.blocked || this.committed) {
      return;
    }
    this.committing = true;
    this.uploadError = null;

    this.catalogService.upload(this.catalogId, this.file)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (plan) => {
          this.plan = plan;
          this.committing = false;
          const t = plan.totals;
          this.uploadSuccess = `Uploaded as version ${plan.version}: ${t.create} new, ${t.update} updated, `
            + `${t.retire} retired, ${t.unchanged} unchanged.`;
          this.loadCatalog();
          this.page = 0;
          this.loadMaterials();
        },
        error: (err) => {
          this.uploadError = this.messageOf(err, 'The upload failed.');
          this.committing = false;
        }
      });
  }

  /** Whether anything in the file stops it being committed. */
  get blocked(): boolean {
    return !!this.plan && (this.plan.fileErrors.length > 0 || this.plan.totals.error > 0);
  }

  get committed(): boolean {
    return !!this.plan && this.plan.committed;
  }

  /** Errors first — they are what has to be fixed — then the file's order, retirements last. */
  get sortedRows(): CatalogUploadRow[] {
    if (!this.plan) {
      return [];
    }
    const rank = (row: CatalogUploadRow) => row.action === 'ERROR' ? 0 : row.action === 'RETIRE' ? 2 : 1;
    return [...this.plan.rows].sort((a, b) =>
      rank(a) - rank(b) || (a.line ?? Number.MAX_SAFE_INTEGER) - (b.line ?? Number.MAX_SAFE_INTEGER));
  }

  actionLabel(action: CatalogRowAction): string {
    return ACTION_LABELS[action] ?? action;
  }

  actionClass(action: CatalogRowAction): string {
    return `action-badge action-${action.toLowerCase()}`;
  }

  private resetUpload(): void {
    this.plan = null;
    this.uploadError = null;
    this.uploadSuccess = null;
  }

  // ── Materials ──────────────────────────────────────────────────────────────

  onSearch(term: string): void {
    this.search$.next(term);
  }

  loadMaterials(): void {
    this.materialsLoading = true;
    this.materialsError = null;

    this.catalogService.getMaterials(this.catalogId, { search: this.search, page: this.page, size: this.pageSize })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.materials = response.data ?? [];
          this.total = response.total ?? 0;
          this.materialsLoading = false;
        },
        error: () => {
          this.materialsError = 'Could not load materials.';
          this.materials = [];
          this.materialsLoading = false;
        }
      });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages || page === this.page) {
      return;
    }
    this.page = page;
    this.loadMaterials();
  }

  unitLabel(unit: UnitOfMeasure | null | undefined): string {
    return unit ? (UNIT_LABELS[unit] ?? unit) : '—';
  }

  packageLabel(pkg: CatalogPackage): string {
    const content = `${pkg.packageQuantity} ${this.unitLabel(pkg.packageUnit)}`;
    const name = pkg.name ? `${pkg.name} (${content})` : content;
    return pkg.manufacturerRef ? `${pkg.manufacturerRef} · ${name}` : name;
  }

  statusLabel(status: CatalogStatus): string {
    return CATALOG_STATUS_LABELS[status] ?? status;
  }

  statusClass(status: CatalogStatus): string {
    return `catalog-status status-${(status || 'draft').toLowerCase()}`;
  }

  trackById(_index: number, item: { id: number }): number {
    return item.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private messageOf(err: any, fallback: string): string {
    return err?.error?.exceptionMessage || err?.error?.message || fallback;
  }
}
